import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus, Package, Tag, Search, History, Pencil, Trash2,
  ChevronDown, ChevronRight, Truck, Euro, TrendingUp, TrendingDown, Scale, MoreVertical, Sparkles, ShieldCheck, Percent,
  Check, X, Download, FileSpreadsheet, ChefHat, UtensilsCrossed, ArrowRight, BarChart3
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { ProdottoDialog, CategoriaDialog, DeleteConfirmDialog, StoricoDialog } from "../../components/acquisti/AcquistiDialogs";
import GestioneCategorie from "../../components/acquisti/GestioneCategorie";
import ConfrontaPrezzoDialog from "../../components/acquisti/ConfrontaPrezzoDialog";
import SistemaOrfaniDialog from "../../components/acquisti/SistemaOrfaniDialog";
import AllergeniManager from "../../components/acquisti/AllergeniManager";
import EditAllergeniDialog from "../../components/acquisti/EditAllergeniDialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "food", label: "Food & Beverage", icon: Package },
  { key: "materiali", label: "Attrezzature & Packaging", icon: Package },
];

import { UNITA_FOOD, UNITA_MATERIALI, labelUdm, prezzoBaseLabel } from "../../utils/udmUtils";

// Dialog storico prezzi + aggiungi prezzo per un prodotto (unificato)
const StoricoPrezziDialog = ({ open, onOpenChange, prodotto, isFood, fornitori, localeId, onSaved, onRefreshFornitori }) => {
  const { token } = useAuth();
  const prezzi = (prodotto?.prezzi_fornitori || []).sort(
    (a, b) => new Date(b.data || b.data_inserimento || b.updated_at || 0) - new Date(a.data || a.data_inserimento || a.updated_at || 0)
  );

  const [showForm, setShowForm] = useState(false);
  const [fornitoreId, setFornitoreId] = useState("");
  const [fornitoreNomeManuale, setFornitoreNomeManuale] = useState("");
  const [prezzo, setPrezzo] = useState("");
  const [data, setData] = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editQta, setEditQta] = useState("");
  const [savingQta, setSavingQta] = useState(false);
  const [editPrezzoIdx, setEditPrezzoIdx] = useState(null);
  const [editPrezzoVal, setEditPrezzoVal] = useState("");
  const [savingPrezzo, setSavingPrezzo] = useState(false);

  useEffect(() => {
    if (open) {
      setShowForm(false); setFornitoreId(""); setFornitoreNomeManuale(""); setPrezzo(""); setData(new Date().toISOString().split("T")[0]);
      setEditIdx(null); setEditQta("");
      setEditPrezzoIdx(null); setEditPrezzoVal("");
      // Ricarica la lista fornitori ad ogni apertura per intercettare i nuovi
      // fornitori creati in un'altra pagina/tab senza un F5 manuale.
      if (typeof onRefreshFornitori === "function") {
        onRefreshFornitori();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fornitoreNome = fornitori.find(f => f.id === fornitoreId)?.ragione_sociale || fornitoreNomeManuale || "Inserimento manuale";
  const unita = prodotto?.unita_misura || (isFood ? "kg" : "pz");

  const handleSave = async () => {
    if (!prezzo || isNaN(parseFloat(prezzo)) || parseFloat(prezzo) <= 0) { toast.error("Inserisci un prezzo valido"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      if (isFood) {
        await fetch(`${API}/api/ingredienti/${prodotto.id}/prezzo`, {
          method: "POST", headers,
          body: JSON.stringify({ ingrediente_id: prodotto.id, fornitore_id: fornitoreId || "manuale", fornitore_nome: fornitoreNome, prezzo_kg: parseFloat(prezzo), locale_id: localeId }),
        });
      } else {
        await fetch(`${API}/api/materiali/aggiorna-prezzo`, {
          method: "POST", headers,
          body: JSON.stringify({ materiale_id: prodotto.id, fornitore_id: fornitoreId || "manuale", fornitore_nome: fornitoreNome, prezzo_unitario: parseFloat(prezzo), data_acquisto: data, fonte: "manuale", locale_id: localeId }),
        });
      }
      toast.success("Prezzo aggiunto");
      setShowForm(false);
      setPrezzo("");
      setFornitoreId("");
      setFornitoreNomeManuale("");
      onSaved();
    } catch { toast.error("Errore nel salvataggio del prezzo"); }
    finally { setSaving(false); }
  };

  const handleDeletePrezzo = async (p) => {
    if (!isFood) {
      toast.error("Cancellazione disponibile solo per ingredienti");
      return;
    }
    const dataPrezzo = (p.data || p.data_inserimento || "").slice(0, 10);
    const dataLabel = dataPrezzo ? new Date(dataPrezzo).toLocaleDateString("it-IT") : "—";
    if (!window.confirm(`Cancellare il prezzo manuale di ${p.fornitore_nome || "fornitore sconosciuto"} del ${dataLabel}?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ fornitore_id: p.fornitore_id || "manuale", data: dataPrezzo });
      const res = await fetch(`${API}/api/ingredienti/${prodotto.id}/prezzo-storico?${params.toString()}`, {
        method: "DELETE", headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossibile cancellare il prezzo");
        return;
      }
      toast.success("Prezzo manuale cancellato");
      onSaved();
    } catch {
      toast.error("Errore durante la cancellazione del prezzo");
    }
  };

  const avviaModificaQta = (p, i) => {
    setEditPrezzoIdx(null);
    setEditIdx(i);
    setEditQta(String(p.quantita_acquistata ?? ""));
  };

  const avviaModificaPrezzo = (p, i) => {
    setEditIdx(null);
    setEditPrezzoIdx(i);
    setEditPrezzoVal(String((isFood ? p.prezzo_kg : p.prezzo_unitario) ?? ""));
  };

  const handleSavePrezzo = async (p) => {
    const nuovo = parseFloat(String(editPrezzoVal).replace(",", "."));
    if (!nuovo || nuovo <= 0) { toast.error("Inserisci un prezzo valido (> 0)"); return; }
    setSavingPrezzo(true);
    try {
      const useCarico = !isFood && p.carico_id;
      const res = await fetch(
        useCarico
          ? `${API}/api/materiali/carichi/${p.carico_id}/prezzo-unitario`
          : `${API}/api/storico-prezzo/prezzo-unitario`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(
            useCarico
              ? { nuovo_prezzo: nuovo }
              : {
                  prodotto_id: prodotto.id,
                  tipo: isFood ? "food" : "materiale",
                  fattura_id: p.fattura_id || null,
                  data_inserimento: (p.data_inserimento || p.data || "").slice(0, 10),
                  fornitore_id: p.fornitore_id || null,
                  nuovo_prezzo: nuovo,
                }
          ),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore durante l'aggiornamento del prezzo");
        return;
      }
      const out = await res.json();
      toast.success(`Prezzo aggiornato: €${Number(out.prezzo_unitario).toFixed(4)}/${unita}`);
      setEditPrezzoIdx(null);
      onSaved();
    } catch {
      toast.error("Errore durante l'aggiornamento del prezzo");
    } finally {
      setSavingPrezzo(false);
    }
  };

  const handleSaveQta = async (p) => {
    const nuova = parseFloat(String(editQta).replace(",", "."));
    if (!nuova || nuova <= 0) { toast.error("Inserisci una quantità valida (> 0)"); return; }
    setSavingQta(true);
    try {
      const useCarico = !isFood && p.carico_id;
      const res = await fetch(
        useCarico
          ? `${API}/api/materiali/carichi/${p.carico_id}/quantita`
          : `${API}/api/storico-prezzo/quantita`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(
            useCarico
              ? { nuova_quantita: nuova }
              : {
                  prodotto_id: prodotto.id,
                  tipo: isFood ? "food" : "materiale",
                  fattura_id: p.fattura_id || null,
                  data_inserimento: (p.data_inserimento || p.data || "").slice(0, 10),
                  fornitore_id: p.fornitore_id || null,
                  nuova_quantita: nuova,
                }
          ),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore durante il ricalcolo");
        return;
      }
      const out = await res.json();
      toast.success(`Quantità corretta: nuovo prezzo €${Number(out.prezzo_unitario).toFixed(4)}/${unita}`);
      setEditIdx(null);
      onSaved();
    } catch {
      toast.error("Errore durante il ricalcolo");
    } finally {
      setSavingQta(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Prezzi — {prodotto?.nome}</DialogTitle>
          <DialogDescription data-testid="price-history-description">
            Storico dei prezzi di acquisto, ordinato dal più recente.
          </DialogDescription>
        </DialogHeader>

        {/* Grafico andamento prezzi */}
        {prezzi.length >= 2 && (() => {
          const chartData = prezzi.slice().reverse()
            .map(p => ({
              label: (p.data || p.data_inserimento)
                ? new Date(p.data || p.data_inserimento).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                : "—",
              price: isFood ? p.prezzo_kg : p.prezzo_unitario,
            }))
            .filter(p => p.price != null);
          if (chartData.length < 2) return null;
          return (
            <div className="h-36 min-w-0 w-full overflow-hidden mb-1">
              <ResponsiveContainer width="100%" height={144} minWidth={0} minHeight={0}>
                <AreaChart data={chartData} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={52}
                    tickFormatter={v => `€${v.toFixed(2)}`}
                  />
                  <Tooltip
                    formatter={v => [`€${v.toFixed(4)}`, "Prezzo"]}
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)" }}
                  />
                  <Area
                    type="monotone" dataKey="price" stroke="#f59e0b" strokeWidth={2}
                    fill="url(#priceGrad)" dot={{ r: 3, fill: "#f59e0b", strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: "#f59e0b" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* Storico prezzi */}
        {prezzi.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Nessun prezzo registrato</p>
        ) : (
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {prezzi.map((p, i) => {
              const val = isFood ? p.prezzo_kg : p.prezzo_unitario;
              const prevVal = prezzi[i + 1] ? (isFood ? prezzi[i + 1].prezzo_kg : prezzi[i + 1].prezzo_unitario) : null;
              const delta = (val && prevVal && prevVal > 0) ? ((val - prevVal) / prevVal) * 100 : null;
              const isManuale = isFood && !p.fattura_id; // cancellabile solo se manuale (no fattura collegata)
              const qtaAcq = p.quantita_acquistata != null ? Number(p.quantita_acquistata) : null;
              const editabile = !!p.fattura_id || qtaAcq != null; // modificabile se ha una quantità/fattura collegata
              const inEdit = editIdx === i;
              const inEditPrezzo = editPrezzoIdx === i;
              return (
                <div key={i} className="p-3 rounded-lg bg-muted/40" data-testid={`riga-prezzo-${i}`}>
                  <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{p.fornitore_nome || "Fornitore sconosciuto"}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs text-muted-foreground">
                        {(p.data || p.data_inserimento) ? new Date(p.data || p.data_inserimento).toLocaleDateString("it-IT") : "—"}
                      </p>
                      {qtaAcq != null && !inEdit && !inEditPrezzo && (
                        <span className="text-[10px] text-muted-foreground">· {qtaAcq} {unita}</span>
                      )}
                      {p.fattura_id ? (
                        <span className="text-[10px] font-semibold bg-indigo-500/15 text-indigo-700 px-1.5 py-0.5 rounded-full">FATTURA</span>
                      ) : isFood ? (
                        <span className="text-[10px] font-semibold bg-slate-500/15 text-slate-600 px-1.5 py-0.5 rounded-full">MANUALE</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {delta !== null && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        delta > 0 ? "bg-orange-500/15 text-orange-600" : "bg-emerald-500/15 text-emerald-600"
                      }`}>
                        {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
                      </span>
                    )}
                    <span className="font-mono font-semibold text-primary">
                      €{(val || 0).toFixed(4)}
                      <span className="text-xs text-muted-foreground ml-1">/{unita}</span>
                    </span>
                    {editabile && !inEdit && !inEditPrezzo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                        onClick={() => avviaModificaQta(p, i)}
                        title="Correggi quantità (totale invariato)"
                        data-testid={`btn-modifica-qta-prezzo-${i}`}
                      >
                        <Scale className="h-4 w-4" strokeWidth={2.25} />
                      </Button>
                    )}
                    {!inEdit && !inEditPrezzo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        onClick={() => avviaModificaPrezzo(p, i)}
                        title="Correggi prezzo unitario (totale ricalcolato)"
                        data-testid={`btn-modifica-prezzo-${i}`}
                      >
                        <Euro className="h-4 w-4" strokeWidth={2.25} />
                      </Button>
                    )}
                    {isManuale && !inEdit && !inEditPrezzo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeletePrezzo(p)}
                        title="Cancella questo prezzo manuale"
                        data-testid={`btn-cancella-prezzo-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  </div>
                  {inEdit && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2" data-testid={`edit-qta-panel-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Quantità:</span>
                        <Input
                          type="number" step="0.0001" min="0" value={editQta}
                          onChange={(e) => setEditQta(e.target.value)}
                          className="h-8 w-28 text-sm rounded-lg font-mono"
                          autoFocus
                          data-testid={`edit-qta-input-${i}`}
                        />
                        <span className="text-xs text-muted-foreground">{unita}</span>
                        <div className="flex gap-1 ml-auto">
                          <Button size="icon" className="h-8 w-8" disabled={savingQta} onClick={() => handleSaveQta(p)} data-testid={`edit-qta-save-${i}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditIdx(null)} data-testid={`edit-qta-cancel-${i}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {(() => {
                        const totaleRiga = (Number(val || 0) * Number(qtaAcq || 0));
                        const qtaLive = parseFloat(String(editQta).replace(",", "."));
                        const prezzoLive = (totaleRiga > 0 && qtaLive > 0) ? (totaleRiga / qtaLive) : null;
                        return (
                          <div className="flex items-center justify-between text-xs">
                            {totaleRiga > 0 && <span className="text-muted-foreground">Totale: <span className="text-emerald-600 font-medium">€{totaleRiga.toFixed(2)}</span></span>}
                            {prezzoLive != null && (
                              <span className="text-muted-foreground">Nuovo prezzo: <span className="text-primary font-semibold" data-testid={`edit-qta-live-${i}`}>€{prezzoLive.toFixed(4)}/{unita}</span></span>
                            )}
                          </div>
                        );
                      })()}
                      <p className="text-[11px] text-muted-foreground">Il totale di spesa resta invariato: il prezzo/{unita} viene ricalcolato sulla nuova quantità.</p>
                    </div>
                  )}
                  {inEditPrezzo && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-2" data-testid={`edit-prezzo-panel-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Prezzo unit.:</span>
                        <Input
                          type="number" step="0.0001" min="0" value={editPrezzoVal}
                          onChange={(e) => setEditPrezzoVal(e.target.value)}
                          className="h-8 w-28 text-sm rounded-lg font-mono"
                          autoFocus
                          data-testid={`edit-prezzo-input-${i}`}
                        />
                        <span className="text-xs text-muted-foreground">€/{unita}</span>
                        <div className="flex gap-1 ml-auto">
                          <Button size="icon" className="h-8 w-8 bg-emerald-600 hover:bg-emerald-700" disabled={savingPrezzo} onClick={() => handleSavePrezzo(p)} data-testid={`edit-prezzo-save-${i}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditPrezzoIdx(null)} data-testid={`edit-prezzo-cancel-${i}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {qtaAcq != null && qtaAcq > 0 && (() => {
                        const prezzoLive = parseFloat(String(editPrezzoVal).replace(",", "."));
                        const totaleLive = (prezzoLive > 0) ? (prezzoLive * qtaAcq) : null;
                        return totaleLive != null ? (
                          <div className="text-xs text-muted-foreground">Nuovo totale: <span className="text-emerald-600 font-semibold" data-testid={`edit-prezzo-live-${i}`}>€{totaleLive.toFixed(2)}</span> ({qtaAcq} {unita})</div>
                        ) : null;
                      })()}
                      <p className="text-[11px] text-muted-foreground">La quantità resta invariata: il totale viene ricalcolato (prezzo × quantità).</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Aggiungi prezzo — toggle form */}
        {!showForm ? (
          <Button variant="outline" className="w-full rounded-xl mt-2" onClick={() => setShowForm(true)} data-testid="btn-mostra-form-prezzo">
            <Plus className="h-4 w-4 mr-2" />Aggiungi prezzo
          </Button>
        ) : (
          <div className="border rounded-xl p-4 space-y-3 mt-2 bg-muted/20">
            <p className="text-sm font-semibold">Nuovo prezzo</p>
            <Select value={fornitoreId || "__manual__"} onValueChange={v => setFornitoreId(v === "__manual__" ? "" : v)}>
              <SelectTrigger className="rounded-xl" data-testid="prezzo-fornitore-select">
                <SelectValue placeholder="Seleziona fornitore" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__manual__">— Inserimento manuale —</SelectItem>
                {fornitori.map(f => <SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
            {!fornitoreId && (
              <Input placeholder="Nome fornitore (opzionale)" value={fornitoreNomeManuale} onChange={e => setFornitoreNomeManuale(e.target.value)} className="rounded-xl" data-testid="prezzo-fornitore-nome" />
            )}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">€</span>
              <Input type="number" step="0.0001" min="0" placeholder={`0.0000 / ${unita}`} value={prezzo} onChange={e => setPrezzo(e.target.value)} className="pl-7 rounded-xl font-mono" data-testid="prezzo-valore-input" />
            </div>
            <DateInput value={data} onChange={(v) => setData(v)} />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button className="flex-1 rounded-xl" onClick={handleSave} disabled={saving} data-testid="prezzo-save-btn">
                {saving ? "Salvataggio..." : "Aggiungi"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RisparmioBadge = ({ prod, onClick }) => {
  if (!(prod.risparmio_percentuale > 0) || !prod.prezzo_migliore_fornitore) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors max-w-[190px]"
      title={`Risparmio potenziale del ${Math.round(prod.risparmio_percentuale)}% passando a ${prod.prezzo_migliore_fornitore}`}
      data-testid={`badge-risparmio-${prod.id}`}
    >
      <TrendingDown className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">−{Math.round(prod.risparmio_percentuale)}% con {prod.prezzo_migliore_fornitore}</span>
    </button>
  );
};

const ProdottiTab = ({ tipo, openId, onConsumeOpen }) => {
  const { localeCorrente, user, token } = useAuth();
  const isFood = tipo === "food";

  const endpoints = useMemo(() => isFood ? {
    prodotti: "/api/ingredienti",
    categorie: "/api/categorie-ingredienti",
    carichi: "/api/movimenti-magazzino",
  } : {
    prodotti: "/api/materiali",
    categorie: "/api/materiali/categorie",
    carichi: "/api/materiali/carichi",
  }, [isFood]);

  const [loading, setLoading] = useState(true);
  const [prodotti, setProdotti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [tipologieFornitori, setTipologieFornitori] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});

  // Dialogs
  const [dialogProdotto, setDialogProdotto] = useState(false);
  const [dialogGestioneCategorie, setDialogGestioneCategorie] = useState(false);
  const [dialogStoricoPrezzi, setDialogStoricoPrezzi] = useState(false);
  const [dialogStoricoAcquisti, setDialogStoricoAcquisti] = useState(false);
  const [dialogCategoria, setDialogCategoria] = useState(false);
  const [formCategoria, setFormCategoria] = useState({ nome: "", icona: "" });
  const [editItem, setEditItem] = useState(null);
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [storicoCarichi, setStoricoCarichi] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formProdotto, setFormProdotto] = useState({
    nome: "", categoria_id: "", fornitore_id: "",
    unita_misura: isFood ? "kg" : "pz", note: "", tipo: "packaging",
    iva_percentuale: isFood ? 10 : 22
  });
  const [dialogIvaBulk, setDialogIvaBulk] = useState(false);
  const [ivaBulkValue, setIvaBulkValue] = useState(isFood ? "10" : "22");
  const [ivaBulkScope, setIvaBulkScope] = useState("__all__");
  const [editIvaId, setEditIvaId] = useState(null);
  const [editIvaVal, setEditIvaVal] = useState("");

  // Dialog "Sistema orfani" (solo per Food: legato a categorie_ingredienti)
  const [orfaniDialogOpen, setOrfaniDialogOpen] = useState(false);
  const [orfaniCount, setOrfaniCount] = useState(0);

  // Dialog "Allergeni" su singolo prodotto
  const [editAllergeniProd, setEditAllergeniProd] = useState(null);
  // Metadata allergeni (14 EU) per render icone+label sulla card
  const [allergeniMeta, setAllergeniMeta] = useState({});
  // "Dove viene usato" — reverse lookup ricette + menu
  const navigate = useNavigate();
  const [utilizzoCounts, setUtilizzoCounts] = useState({});
  const [utilizzoProd, setUtilizzoProd] = useState(null);
  const [utilizzoData, setUtilizzoData] = useState(null);
  const [utilizzoLoading, setUtilizzoLoading] = useState(false);
  useEffect(() => {
    if (!isFood || !token) return;
    fetch(`${API}/api/allergeni/metadata`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((arr) => {
        const map = {};
        (arr || []).forEach((a) => { map[a.key] = a; });
        setAllergeniMeta(map);
      })
      .catch(() => {});
  }, [isFood, token]);
  const fetchOrfaniCount = useCallback(async () => {
    if (!isFood || !localeCorrente?.id || !token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API}/api/ingredienti/orfani-fix?locale_id=${localeCorrente.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOrfaniCount((data.orfani || []).length);
      }
    } catch { /* silenzioso */ }
  }, [isFood, localeCorrente?.id, token]);
  useEffect(() => { fetchOrfaniCount(); }, [fetchOrfaniCount]);

  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const lp = `locale_id=${localeCorrente.id}`;
      const [pRes, cRes, fRes, tRes] = await Promise.all([
        fetch(`${API}${endpoints.prodotti}?${lp}`, { headers }),
        fetch(`${API}${endpoints.categorie}?${lp}`, { headers }),
        fetch(`${API}/api/fornitori`, { headers }),
        fetch(`${API}/api/tipologie-fornitore`, { headers }),
      ]);
      if (pRes.ok) setProdotti(await pRes.json());
      if (cRes.ok) setCategorie(await cRes.json());
      if (fRes.ok) {
        const data = await fRes.json();
        setFornitori(data.sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || "", "it")));
      }
      if (tRes.ok) setTipologieFornitori(await tRes.json());
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, token, endpoints]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Conteggi "dove viene usato" (una call, per i badge)
  const fetchUtilizzoCounts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/prodotti/utilizzo-counts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUtilizzoCounts(await res.json());
    } catch (e) { /* silent */ }
  }, [token]);
  useEffect(() => { fetchUtilizzoCounts(); }, [fetchUtilizzoCounts]);

  const openUtilizzo = useCallback(async (prod) => {
    setUtilizzoProd(prod);
    setUtilizzoData(null);
    setUtilizzoLoading(true);
    try {
      const res = await fetch(`${API}/api/prodotti/${prod.id}/utilizzo`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUtilizzoData(await res.json());
      else setUtilizzoData({ ricette: [], menu: [] });
    } catch (e) {
      setUtilizzoData({ ricette: [], menu: [] });
    } finally {
      setUtilizzoLoading(false);
    }
  }, [token]);

  // Refresh leggero della sola lista fornitori (usato dai dialog di inserimento prezzo
  // per intercettare nuovi fornitori creati in un'altra pagina senza F5).
  const refreshFornitori = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/fornitori`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFornitori(data.sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || "", "it")));
      }
    } catch { /* silenzioso */ }
  }, [token]);

  const prodottiFiltrati = prodotti.filter(p =>
    p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!categoriaFilter || p.categoria_id === categoriaFilter)
  );

  const openStoricoPrezzi = (prod) => { setSelectedProdotto(prod); setDialogStoricoPrezzi(true); };
  const [dialogConfronta, setDialogConfronta] = useState(false);
  const [prodottoConfronta, setProdottoConfronta] = useState(null);
  const openConfronta = (prod) => { setProdottoConfronta(prod); setDialogConfronta(true); };

  // Deep-link dagli alert Dashboard: /acquisti/prodotti?open={id}&tab={food|materiali}
  // → espande la categoria, scrolla al prodotto e apre lo storico prezzi.
  const handledOpenRef = useRef(null);
  useEffect(() => {
    if (!openId || loading || handledOpenRef.current === openId) return;
    const prod = prodotti.find((p) => p.id === openId);
    if (!prod) return;
    handledOpenRef.current = openId;
    if (prod.categoria_id) {
      setExpandedCategories((prev) => ({ ...prev, [prod.categoria_id]: true }));
    }
    setTimeout(() => {
      document.querySelector(`[data-testid="prodotto-${openId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      openStoricoPrezzi(prod);
      if (typeof onConsumeOpen === "function") onConsumeOpen();
    }, 350);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, prodotti, loading]);

  // Apre il dialog di modifica del prodotto. Se la categoria salvata è "orfana"
  // (cancellata dopo il salvataggio), resetta il campo per mostrare il placeholder.
  const openEditProdotto = (prod) => {
    const categorieIds = new Set(categorie.map(c => c.id));
    const catIdValid = prod.categoria_id && categorieIds.has(prod.categoria_id) ? prod.categoria_id : "";
    setEditItem(prod);
    setFormProdotto({
      nome: prod.nome,
      categoria_id: catIdValid,
      fornitore_id: prod.fornitore_id || "",
      unita_misura: prod.unita_misura || (isFood ? "kg" : "pz"),
      note: prod.note || "",
      tipo: prod.tipo || "packaging",
      iva_percentuale: prod.iva_percentuale ?? (isFood ? 10 : 22),
    });
    setDialogProdotto(true);
  };

  // Sincronizza selectedProdotto quando prodotti si aggiorna (es. dopo aggiunta prezzo nel dialog)
  useEffect(() => {
    if (selectedProdotto) {
      const aggiornato = prodotti.find(p => p.id === selectedProdotto.id);
      if (aggiornato) setSelectedProdotto(aggiornato);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodotti]);
  const getPriceDelta = (prod) => {
    const prezzi = (prod.prezzi_fornitori || [])
      .map(p => ({
        value: isFood ? p.prezzo_kg : p.prezzo_unitario,
        date: p.data || p.data_inserimento || p.updated_at || ""
      }))
      .filter(p => p.value != null && p.value > 0)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (prezzi.length < 2) return null;
    return ((prezzi[0].value - prezzi[1].value) / prezzi[1].value) * 100;
  };

  const openStoricoAcquisti = async (prod) => {
    setSelectedProdotto(prod);
    setDialogStoricoAcquisti(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const url = isFood
        ? `${API}/api/movimenti-magazzino?locale_id=${localeCorrente.id}&ingrediente_id=${prod.id}&tipo=carico`
        : `${API}${endpoints.carichi}?materiale_id=${prod.id}`;
      const res = await fetch(url, { headers });
      if (res.ok) setStoricoCarichi(await res.json());
      else setStoricoCarichi([]);
    } catch { setStoricoCarichi([]); }
  };

  const saveProdotto = async () => {
    if (!formProdotto.nome.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const ivaNum = parseFloat(String(formProdotto.iva_percentuale));
      const body = { ...formProdotto, iva_percentuale: (!isNaN(ivaNum) && ivaNum >= 0) ? ivaNum : (isFood ? 10 : 22), locale_id: localeCorrente?.id };
      if (editItem) {
        await fetch(`${API}${endpoints.prodotti}/${editItem.id}`, { method: "PUT", headers, body: JSON.stringify(body) });
        toast.success("Prodotto aggiornato");
      } else {
        await fetch(`${API}${endpoints.prodotti}`, { method: "POST", headers, body: JSON.stringify(body) });
        toast.success("Prodotto creato");
      }
      setDialogProdotto(false);
      setEditItem(null);
      setFormProdotto({ nome: "", categoria_id: "", fornitore_id: "", unita_misura: isFood ? "kg" : "pz", note: "", tipo: "packaging" });
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const saveIvaInline = async (prod) => {
    const iva = parseFloat(String(editIvaVal).replace(",", "."));
    if (isNaN(iva) || iva < 0) { toast.error("Aliquota IVA non valida"); return; }
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const url = `${API}${endpoints.prodotti}/${prod.id}`;
      const res = await fetch(url, { method: "PUT", headers, body: JSON.stringify({ iva_percentuale: iva }) });
      if (!res.ok) { toast.error("Errore aggiornamento IVA"); return; }
      toast.success(`IVA aggiornata: ${iva}%`);
      setEditIvaId(null);
      fetchData();
    } catch { toast.error("Errore aggiornamento IVA"); }
  };

  const saveIvaBulk = async () => {
    const iva = parseFloat(String(ivaBulkValue).replace(",", "."));
    if (isNaN(iva) || iva < 0) { toast.error("Inserisci un'aliquota IVA valida"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const body = {
        tipo: isFood ? "food" : "materiale",
        iva_percentuale: iva,
        locale_id: localeCorrente?.id,
      };
      if (ivaBulkScope !== "__all__") body.categoria_id = ivaBulkScope;
      const res = await fetch(`${API}/api/prodotti/iva-bulk`, { method: "POST", headers, body: JSON.stringify(body) });
      const out = await res.json();
      if (!res.ok) { toast.error(out.detail || "Errore"); return; }
      toast.success(`IVA ${iva}% impostata su ${out.updated} prodotti`);
      setDialogIvaBulk(false);
      fetchData();
    } catch { toast.error("Errore nell'aggiornamento IVA"); }
    finally { setSaving(false); }
  };

  const saveCategoria = async () => {
    if (!formCategoria.nome.trim()) { toast.error("Inserisci un nome per la categoria"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const body = { ...formCategoria, locale_id: localeCorrente?.id };
      if (editItem) {
        await fetch(`${API}${endpoints.categorie}/${editItem.id}`, { method: "PUT", headers, body: JSON.stringify(body) });
        toast.success("Categoria aggiornata");
      } else {
        await fetch(`${API}${endpoints.categorie}`, { method: "POST", headers, body: JSON.stringify(body) });
        toast.success("Categoria creata");
      }
      setDialogCategoria(false);
      setEditItem(null);
      setFormCategoria({ nome: "", icona: "" });
      fetchData();
    } catch { toast.error("Errore nel salvataggio della categoria"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId || !deleteType) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const url = deleteType === "prodotto"
        ? `${API}${endpoints.prodotti}/${deleteId}`
        : `${API}${endpoints.categorie}/${deleteId}`;
      const response = await fetch(url, { method: "DELETE", headers });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = result.detail || "Impossibile completare l'eliminazione.";
        setDeleteError(detail);
        toast.error(detail);
        return;
      }
      toast.success("Eliminato con successo");
      setDeleteId(null);
      setDeleteType(null);
      fetchData();
    } catch {
      const detail = "Errore di connessione durante l'eliminazione.";
      setDeleteError(detail);
      toast.error(detail);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground animate-pulse">Caricamento...</div>;

  return (
    <div className="space-y-4 pb-24 sm:pb-4">
      {/* Toolbar: riga 1 = search + Prodotto, riga 2 = filtri + azioni secondarie */}
      <div className="sticky top-0 z-30 -mx-4 sm:mx-0 px-4 sm:px-0 py-2 sm:py-0 bg-background/95 backdrop-blur-xl border-b border-border/50 sm:border-none space-y-2">
        {/* Riga 1: Search + Nuovo Prodotto */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca prodotto..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 h-11 rounded-2xl bg-background border-border focus-visible:ring-primary/20"
              data-testid="prodotti-search"
            />
          </div>
          <Button
            onClick={() => { setEditItem(null); setFormProdotto({ nome: "", categoria_id: "", fornitore_id: "", unita_misura: isFood ? "kg" : "pz", note: "", tipo: "packaging" }); setDialogProdotto(true); }}
            className="h-11 rounded-2xl shrink-0 hidden sm:inline-flex"
            data-testid="btn-nuovo-prodotto-desktop"
          >
            <Plus className="h-4 w-4 mr-2" />Prodotto
          </Button>
        </div>
        {/* Riga 2: Categoria + Categorie + Sistema */}
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={categoriaFilter || "__all__"} onValueChange={v => setCategoriaFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="flex-1 sm:w-[200px] sm:flex-none h-10 rounded-xl bg-background border-border text-sm" data-testid="categoria-select">
              <SelectValue placeholder="Tutte le categorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte le categorie</SelectItem>
              {categorie.map(c => <SelectItem key={c.id} value={c.id}>{c.icona || (isFood ? "🥗" : "📦")} {c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setDialogGestioneCategorie(true)}
            className="h-10 px-3 rounded-xl shrink-0 text-sm"
            data-testid="btn-gestione-categorie"
          >
            <Tag className="h-4 w-4" /><span className="ml-2 hidden sm:inline">Categorie</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => { setIvaBulkScope(categoriaFilter || "__all__"); setIvaBulkValue(isFood ? "10" : "22"); setDialogIvaBulk(true); }}
            className="h-10 px-3 rounded-xl shrink-0 text-sm"
            data-testid="btn-iva-bulk"
          >
            <Percent className="h-4 w-4" /><span className="ml-2 hidden sm:inline">IVA</span>
          </Button>
          {isFood && orfaniCount > 0 && (
            <button
              type="button"
              onClick={() => setOrfaniDialogOpen(true)}
              className="h-10 px-3 rounded-xl shrink-0 inline-flex items-center font-medium text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 active:scale-[0.98] transition-colors"
              data-testid="btn-sistema-orfani-prodotti"
            >
              <Sparkles className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Sistema</span>
              <span className="ml-1 text-xs font-mono rounded-full px-1.5 py-0.5" style={{ backgroundColor: "#4338CA", color: "#FFFFFF" }}>{orfaniCount}</span>
            </button>
          )}
        </div>
      </div>

      {/* Banner allergeni HACCP — solo per Food */}
      {isFood && localeCorrente?.id && (
        <AllergeniManager localeId={localeCorrente.id} onChanged={fetchData} />
      )}

      {/* Lista per categoria */}
      <div className="space-y-3">
        {categorie.map(cat => {
          const catProd = prodottiFiltrati.filter(p => p.categoria_id === cat.id);
          if (catProd.length === 0) return null;
          const isExpanded = expandedCategories[cat.id] !== false;
          return (
            <Card key={cat.id}>
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 rounded-t-lg min-h-[56px]"
                onClick={() => setExpandedCategories(prev => ({ ...prev, [cat.id]: !isExpanded }))}
                data-testid={`categoria-${cat.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-xl">{cat.icona || (isFood ? "🥗" : "📦")}</span>
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{cat.nome}</h3>
                    <p className="text-xs text-muted-foreground">{catProd.length} prodotti</p>
                  </div>
                </div>
                <div className="shrink-0 ml-2 h-11 w-11 flex items-center justify-center rounded-lg">
                  {isExpanded ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                </div>
              </div>

              {isExpanded && (
                <CardContent className="pt-0 space-y-2 pb-3">
                  {catProd.map(prod => {
                    const prezzo = isFood ? (prod.prezzo_attuale || prod.prezzo_kg) : (prod.prezzo_ultimo || prod.prezzo_unitario);
                    const fornitoreNome = fornitori.find(f => f.id === prod.fornitore_id)?.ragione_sociale;
                    const delta = getPriceDelta(prod);
                    const hasAlert = delta !== null && delta > 5;
                    return (
                      <div key={prod.id}
                        className={`flex items-start justify-between p-3 rounded-xl border transition-colors ${
                          hasAlert
                            ? "bg-orange-500/10 hover:bg-orange-500/15 border-orange-500/25"
                            : "bg-white hover:bg-muted/40 border-slate-200"
                        }`}
                        data-testid={`prodotto-${prod.id}`}
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0 pr-2">
                          <Package className={`h-4 w-4 shrink-0 mt-1 ${hasAlert ? "text-orange-500" : "text-muted-foreground"}`} />
                          <div className="flex-1 min-w-0 space-y-1">
                            <button
                              type="button"
                              onClick={() => openEditProdotto(prod)}
                              className="font-semibold text-sm sm:text-base truncate leading-tight text-left w-full hover:underline hover:text-indigo-700 transition-colors cursor-pointer"
                              data-testid={`btn-nome-prodotto-${prod.id}`}
                              title="Modifica prodotto"
                            >
                              {prod.nome}
                            </button>
                            <div className="flex items-center gap-2 flex-wrap">
                              {prezzo ? (
                                <span className="flex items-center gap-0.5 text-emerald-600 font-mono font-bold text-sm">
                                  <Euro className="h-3 w-3" />{prezzo.toFixed(4)}
                                  <span className="text-muted-foreground font-normal text-xs">/{prod.unita_misura || "pz"}</span>
                                </span>
                              ) : (
                                <span className="text-xs italic text-muted-foreground">nessun prezzo</span>
                              )}
                              {editIvaId === prod.id ? (
                                <span className="inline-flex items-center gap-1">
                                  <Input
                                    type="number" step="0.5" min="0" value={editIvaVal}
                                    onChange={(e) => setEditIvaVal(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveIvaInline(prod); }}
                                    className="h-6 w-16 text-xs px-1"
                                    autoFocus
                                    data-testid={`iva-inline-input-${prod.id}`}
                                  />
                                  <button onClick={() => saveIvaInline(prod)} className="text-emerald-600" data-testid={`iva-inline-save-${prod.id}`}><Check className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => setEditIvaId(null)} className="text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setEditIvaId(prod.id); setEditIvaVal(String(prod.iva_percentuale ?? (isFood ? 10 : 22))); }}
                                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  data-testid={`badge-iva-${prod.id}`}
                                  title="Modifica aliquota IVA (usata per il food cost)"
                                >
                                  <Percent className="h-2.5 w-2.5" />IVA {prod.iva_percentuale ?? (isFood ? 10 : 22)}%
                                </button>
                              )}
                              {delta !== null && Math.abs(delta) > 5 && (
                                <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                  delta > 0
                                    ? "bg-orange-500/20 text-orange-700"
                                    : "bg-emerald-500/20 text-emerald-700"
                                }`}>
                                  {delta > 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                                  {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
                                </span>
                              )}
                              <RisparmioBadge prod={prod} onClick={() => openStoricoPrezzi(prod)} />
                              {(() => {
                                const u = utilizzoCounts[prod.id];
                                const nR = u?.ricette || 0;
                                const nM = u?.menu || 0;
                                if (!nR && !nM) return null;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => openUtilizzo(prod)}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-700 hover:bg-indigo-500/20 transition-colors"
                                    data-testid={`badge-utilizzo-${prod.id}`}
                                    title="Dove viene usato"
                                  >
                                    {nR > 0 && <span className="inline-flex items-center gap-0.5"><ChefHat className="h-2.5 w-2.5" />{nR}</span>}
                                    {nR > 0 && nM > 0 && <span className="text-indigo-300">·</span>}
                                    {nM > 0 && <span className="inline-flex items-center gap-0.5"><UtensilsCrossed className="h-2.5 w-2.5" />{nM}</span>}
                                  </button>
                                );
                              })()}
                            </div>
                            {fornitoreNome && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                                <Truck className="h-3 w-3 shrink-0" /><span className="truncate">{fornitoreNome}</span>
                              </span>
                            )}
                            {isFood && Array.isArray(prod.allergeni) && prod.allergeni.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setEditAllergeniProd(prod)}
                                className="flex items-center gap-1 flex-wrap pt-0.5 hover:opacity-80 transition-opacity text-left"
                                title="Clicca per modificare"
                                data-testid={`allergeni-${prod.id}`}
                              >
                                {prod.allergeni.slice(0, 4).map((a) => {
                                  const meta = allergeniMeta[a];
                                  return (
                                    <span
                                      key={a}
                                      className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium inline-flex items-center gap-0.5 ${
                                        prod.allergeni_verified
                                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                          : "bg-amber-50 border-amber-200 text-amber-700"
                                      }`}
                                    >
                                      {meta ? <><span>{meta.icon}</span><span>{meta.label}</span></> : a}
                                    </span>
                                  );
                                })}
                                {prod.allergeni.length > 4 && (
                                  <span className="text-[10px] text-muted-foreground">+{prod.allergeni.length - 4}</span>
                                )}
                                {!prod.allergeni_verified && (
                                  <span className="text-[10px] text-amber-600 italic">da confermare</span>
                                )}
                              </button>
                            )}
                            {isFood && prod.allergeni_source && (!prod.allergeni || prod.allergeni.length === 0) && prod.allergeni_verified && (
                              <button
                                type="button"
                                onClick={() => setEditAllergeniProd(prod)}
                                className="text-[10px] text-emerald-600 italic flex items-center gap-1 hover:underline"
                                title="Clicca per modificare"
                              >
                                <ShieldCheck className="h-2.5 w-2.5" />
                                Nessun allergene
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Solo 2 azioni: Prezzi (primary) + Menu overflow */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            title="Prezzi"
                            onClick={() => openStoricoPrezzi(prod)}
                            className="h-11 w-11 p-0 rounded-lg"
                            data-testid={`btn-storico-prezzi-${prod.id}`}
                          >
                            <Euro className="h-5 w-5 text-amber-500" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-11 w-11 p-0 rounded-lg" data-testid={`btn-menu-${prod.id}`}>
                                <MoreVertical className="h-5 w-5 text-muted-foreground" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              {isFood && (
                                <DropdownMenuItem onClick={() => openConfronta(prod)} data-testid={`btn-confronta-${prod.id}`}>
                                  <Scale className="h-4 w-4 mr-2 text-indigo-500" />Confronta prezzo
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => openStoricoAcquisti(prod)} data-testid={`btn-storico-acquisti-${prod.id}`}>
                                <History className="h-4 w-4 mr-2 text-blue-500" />Storico acquisti
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openUtilizzo(prod)} data-testid={`btn-utilizzo-${prod.id}`}>
                                <UtensilsCrossed className="h-4 w-4 mr-2 text-indigo-500" />Dove viene usato
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditProdotto(prod)} data-testid={`btn-modifica-${prod.id}`}>
                                <Pencil className="h-4 w-4 mr-2" />Modifica
                              </DropdownMenuItem>
                              {isFood && (
                                <DropdownMenuItem onClick={() => setEditAllergeniProd(prod)} data-testid={`btn-allergeni-${prod.id}`}>
                                  <ShieldCheck className="h-4 w-4 mr-2 text-emerald-500" />Allergeni HACCP
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => { setDeleteId(prod.id); setDeleteType("prodotto"); setDeleteError(""); }}
                                className="text-red-600 focus:text-red-700"
                                data-testid={`delete-menu-item-categorized-${prod.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                  {/* Azioni categoria + aggiungi prodotto: in fondo, ben distanziate */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <Button
                      variant="ghost"
                      className="flex-1 min-h-[44px] border-dashed border rounded-xl"
                      onClick={() => { setEditItem(null); setFormProdotto({ nome: "", categoria_id: cat.id, fornitore_id: "", unita_misura: isFood ? "kg" : "pz", note: "", tipo: "packaging" }); setDialogProdotto(true); }}
                    >
                      <Plus className="h-4 w-4 mr-2" />Aggiungi in {cat.nome}
                    </Button>
                    <Button
                      variant="outline"
                      className="sm:w-auto min-h-[44px] rounded-xl text-xs"
                      onClick={() => { setEditItem(cat); setFormCategoria({ nome: cat.nome, icona: cat.icona || (isFood ? "🥗" : "📦") }); setDialogCategoria(true); }}
                      data-testid={`btn-edit-categoria-${cat.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" />Modifica categoria
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Senza categoria — include ingredienti con categoria_id orfano (categoria cancellata) */}
        {(() => {
          const categorieIds = new Set(categorie.map(c => c.id));
          const senzaCategoria = prodottiFiltrati.filter(p => !p.categoria_id || !categorieIds.has(p.categoria_id));
          if (senzaCategoria.length === 0) return null;
          return (
          <Card data-testid="card-senza-categoria">
            <div className="p-4 font-semibold text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" /> Senza categoria
              <Badge variant="outline">{senzaCategoria.length}</Badge>
            </div>
            <CardContent className="pt-0 space-y-2 pb-3">
              {senzaCategoria.map(prod => {
                const prezzo = isFood ? (prod.prezzo_attuale || prod.prezzo_kg) : (prod.prezzo_ultimo || prod.prezzo_unitario);
                const fornitoreNome = fornitori.find(f => f.id === prod.fornitore_id)?.ragione_sociale;
                const delta = getPriceDelta(prod);
                const hasAlert = delta !== null && delta > 5;
                return (
                  <div key={prod.id}
                    className={`flex items-start justify-between p-3 rounded-xl border transition-colors ${
                      hasAlert
                        ? "bg-orange-500/10 hover:bg-orange-500/15 border-orange-500/25"
                        : "bg-white hover:bg-muted/40 border-slate-200"
                    }`}
                    data-testid={`prodotto-${prod.id}`}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0 pr-2">
                      <Package className={`h-4 w-4 shrink-0 mt-1 ${hasAlert ? "text-orange-500" : "text-muted-foreground"}`} />
                      <div className="flex-1 min-w-0 space-y-1">
                        <button
                          type="button"
                          onClick={() => openEditProdotto(prod)}
                          className="font-semibold text-sm sm:text-base truncate leading-tight text-left w-full hover:underline hover:text-indigo-700 transition-colors cursor-pointer"
                          data-testid={`btn-nome-prodotto-${prod.id}`}
                          title="Modifica prodotto"
                        >
                          {prod.nome}
                        </button>
                        <div className="flex items-center gap-2 flex-wrap">
                          {prezzo ? (
                            <span className="flex items-center gap-0.5 text-emerald-600 font-mono font-bold text-sm">
                              <Euro className="h-3 w-3" />{prezzo.toFixed(4)}
                              <span className="text-muted-foreground font-normal text-xs">/{labelUdm(prod.unita_misura) || "pz"}</span>
                            </span>
                          ) : (
                            <span className="text-xs italic text-muted-foreground">nessun prezzo</span>
                          )}
                          {delta !== null && Math.abs(delta) > 5 && (
                            <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              delta > 0 ? "bg-orange-500/20 text-orange-700" : "bg-emerald-500/20 text-emerald-700"
                            }`}>
                              {delta > 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                              {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
                            </span>
                          )}
                          <RisparmioBadge prod={prod} onClick={() => openStoricoPrezzi(prod)} />
                        </div>
                        {fornitoreNome && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                            <Truck className="h-3 w-3 shrink-0" /><span className="truncate">{fornitoreNome}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        title="Prezzi"
                        onClick={() => openStoricoPrezzi(prod)}
                        className="h-11 w-11 p-0 rounded-lg"
                        data-testid={`btn-storico-prezzi-${prod.id}`}
                      >
                        <Euro className="h-5 w-5 text-amber-500" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-11 w-11 p-0 rounded-lg" data-testid={`btn-menu-${prod.id}`}>
                            <MoreVertical className="h-5 w-5 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {isFood && (
                            <DropdownMenuItem onClick={() => openConfronta(prod)} data-testid={`btn-confronta-${prod.id}`}>
                              <Scale className="h-4 w-4 mr-2 text-indigo-500" />Confronta prezzo
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => openStoricoAcquisti(prod)} data-testid={`btn-storico-acquisti-${prod.id}`}>
                            <History className="h-4 w-4 mr-2 text-blue-500" />Storico acquisti
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditProdotto(prod)} data-testid={`btn-modifica-${prod.id}`}>
                            <Pencil className="h-4 w-4 mr-2" />Modifica
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => { setDeleteId(prod.id); setDeleteType("prodotto"); setDeleteError(""); }}
                            className="text-red-600 focus:text-red-700"
                            data-testid={`delete-menu-item-uncategorized-${prod.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
          );
        })()}

        {prodottiFiltrati.length === 0 && !loading && (
          <div className="py-12 text-center text-muted-foreground">
            {searchTerm ? "Nessun prodotto trovato" : "Nessun prodotto ancora. Creane uno!"}
          </div>
        )}
      </div>

      {/* FAB mobile: Nuovo Prodotto */}
      <Button
        onClick={() => { setEditItem(null); setFormProdotto({ nome: "", categoria_id: "", fornitore_id: "", unita_misura: isFood ? "kg" : "pz", note: "", tipo: "packaging" }); setDialogProdotto(true); }}
        className="sm:hidden fixed bottom-6 right-6 h-14 w-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl flex items-center justify-center z-40 transition-transform active:scale-90 p-0"
        data-testid="btn-nuovo-prodotto"
        aria-label="Nuovo prodotto"
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Dialogs */}
      <ProdottoDialog open={dialogProdotto} onOpenChange={setDialogProdotto} editItem={editItem} form={formProdotto} setForm={setFormProdotto} onSave={saveProdotto} saving={saving} isFood={isFood} unitaMisura={isFood ? UNITA_FOOD : UNITA_MATERIALI} categorie={categorie} />

      <Dialog open={dialogIvaBulk} onOpenChange={setDialogIvaBulk}>
        <DialogContent className="bg-card border-border" data-testid="dialog-iva-bulk">
          <DialogHeader>
            <DialogTitle>Imposta IVA in blocco</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Applica a</label>
              <Select value={ivaBulkScope} onValueChange={setIvaBulkScope}>
                <SelectTrigger className="mt-1" data-testid="iva-bulk-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i prodotti {isFood ? "(Food)" : "(Packaging)"}</SelectItem>
                  {categorie.map(c => <SelectItem key={c.id} value={c.id}>{c.icona || (isFood ? "🥗" : "📦")} {c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Aliquota IVA (%)</label>
              <Input
                type="number" step="0.5" min="0"
                value={ivaBulkValue}
                onChange={(e) => setIvaBulkValue(e.target.value)}
                className="mt-1"
                data-testid="iva-bulk-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Verrà applicata a tutti i prodotti selezionati e usata nel calcolo del food cost.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogIvaBulk(false)}>Annulla</Button>
            <Button onClick={saveIvaBulk} disabled={saving} data-testid="iva-bulk-save">{saving ? "Salvataggio..." : "Applica"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoriaDialog open={dialogCategoria} onOpenChange={setDialogCategoria} editItem={editItem} form={formCategoria} setForm={setFormCategoria} onSave={saveCategoria} saving={saving} isFood={isFood} />
      <GestioneCategorie open={dialogGestioneCategorie} onOpenChange={setDialogGestioneCategorie} isFood={isFood} categorie={categorie} localeId={localeCorrente?.id} onRefresh={fetchData} prodotti={prodotti} />
      <SistemaOrfaniDialog
        open={orfaniDialogOpen}
        onClose={() => setOrfaniDialogOpen(false)}
        mode="ingredienti"
        getAuthHeaders={() => ({ Authorization: `Bearer ${token}` })}
        localeId={localeCorrente?.id}
        onSaved={() => { fetchData(); fetchOrfaniCount(); }}
      />
      <StoricoPrezziDialog open={dialogStoricoPrezzi} onOpenChange={setDialogStoricoPrezzi} prodotto={selectedProdotto} isFood={isFood} fornitori={fornitori} localeId={localeCorrente?.id} onSaved={fetchData} onRefreshFornitori={refreshFornitori} />
      <StoricoDialog open={dialogStoricoAcquisti} onOpenChange={setDialogStoricoAcquisti} storicoProdotto={selectedProdotto} storicoCarichi={storicoCarichi} isFood={isFood} token={token} onUpdated={() => { openStoricoAcquisti(selectedProdotto); fetchData(); }} />
      <DeleteConfirmDialog
        deleteId={deleteId}
        error={deleteError}
        deleting={deleting}
        onClose={() => {
          if (deleting) return;
          setDeleteId(null);
          setDeleteType(null);
          setDeleteError("");
        }}
        onDelete={handleDelete}
      />
      <ConfrontaPrezzoDialog
        open={dialogConfronta}
        onOpenChange={setDialogConfronta}
        prodotto={prodottoConfronta}
        fornitori={fornitori}
        localeId={localeCorrente?.id}
        onSaved={fetchData}
      />
      <EditAllergeniDialog
        prod={editAllergeniProd}
        open={!!editAllergeniProd}
        onClose={() => setEditAllergeniProd(null)}
        onSaved={fetchData}
      />

      {/* Dialog "Dove viene usato" */}
      <Dialog open={!!utilizzoProd} onOpenChange={(o) => { if (!o) { setUtilizzoProd(null); setUtilizzoData(null); } }}>
        <DialogContent className="max-w-md" data-testid="utilizzo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5 text-indigo-500" />
              Dove viene usato
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground -mt-1">{utilizzoProd?.nome}</p>
            {utilizzoLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Caricamento…</div>
            ) : (
              <>
                {/* Ricette */}
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <ChefHat className="h-3.5 w-3.5 text-amber-600" />Ricette ({utilizzoData?.ricette?.length || 0})
                  </p>
                  {(utilizzoData?.ricette || []).length === 0 ? (
                    <p className="text-xs italic text-muted-foreground pl-1">Non usato in nessuna ricetta</p>
                  ) : (
                    <div className="space-y-1.5">
                      {utilizzoData.ricette.map(r => (
                        <button
                          key={r.id}
                          onClick={() => navigate(`/anagrafica/ricette?open=${r.id}`)}
                          className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left group"
                          data-testid={`utilizzo-ricetta-${r.id}`}
                        >
                          <span className="text-sm text-foreground truncate">
                            {r.nome}
                            {!r.diretto && <span className="ml-1.5 text-[10px] text-muted-foreground">(via sotto-ricetta)</span>}
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Menù */}
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <UtensilsCrossed className="h-3.5 w-3.5 text-indigo-600" />Menù di vendita ({utilizzoData?.menu?.length || 0})
                  </p>
                  {(utilizzoData?.menu || []).length === 0 ? (
                    <p className="text-xs italic text-muted-foreground pl-1">Non usato in nessun menù</p>
                  ) : (
                    <div className="space-y-1.5">
                      {utilizzoData.menu.map(m => (
                        <button
                          key={m.id}
                          onClick={() => navigate(`/menu?open=${m.id}`)}
                          className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left group"
                          data-testid={`utilizzo-menu-${m.id}`}
                        >
                          <span className="text-sm text-foreground truncate">
                            {m.nome}
                            {m.via === "ricetta" && <span className="ml-1.5 text-[10px] text-muted-foreground">(via {m.ricetta_nome || "ricetta"})</span>}
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dialog export Excel acquisti: seleziona periodo → scarica xlsx
const ExportAcquistiDialog = ({ open, onOpenChange }) => {
  const { token, localeCorrente } = useAuth();
  const oggi = new Date();
  const trentaGgFa = new Date(oggi.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [dataInizio, setDataInizio] = useState(trentaGgFa.toISOString().split("T")[0]);
  const [dataFine, setDataFine] = useState(oggi.toISOString().split("T")[0]);
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    if (!localeCorrente?.id) { toast.error("Seleziona un locale"); return; }
    if (dataInizio > dataFine) { toast.error("La data inizio non può essere dopo la data fine"); return; }
    setDownloading(true);
    try {
      const params = new URLSearchParams({ locale_id: localeCorrente.id, data_inizio: dataInizio, data_fine: dataFine });
      const res = await fetch(`${API}/api/acquisti/export-prodotti?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore durante l'export");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `acquisti_prodotti_${dataInizio}_${dataFine}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel scaricato");
      onOpenChange(false);
    } catch {
      toast.error("Errore durante l'export");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-acquisti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Esporta prodotti acquistati
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Genera un Excel con i prodotti acquistati nel periodo scelto: tipologia, nome,
            totale quantità (kg/lt/pz) e fornitori utilizzati.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Data inizio</label>
              <DateInput value={dataInizio} onChange={setDataInizio} data-testid="export-data-inizio" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Data fine</label>
              <DateInput value={dataFine} onChange={setDataFine} data-testid="export-data-fine" />
            </div>
          </div>
          <Button
            className="w-full rounded-xl"
            onClick={handleExport}
            disabled={downloading}
            data-testid="export-download-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? "Generazione..." : "Scarica Excel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProdottiPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const [activeTab, setActiveTab] = useState(() => (searchParams.get("tab") === "materiali" ? "materiali" : "food"));
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "materiali" || t === "food") setActiveTab(t);
  }, [searchParams]);

  const consumeOpen = () => setSearchParams({}, { replace: true });

  return (
    <div className="space-y-6" data-testid="prodotti-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="hidden md:block">
          <h1 className="text-3xl font-bold text-foreground">Prodotti</h1>
          <p className="text-muted-foreground mt-1">Catalogo prodotti, prezzi e storico acquisti</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            onClick={() => navigate("/acquisti/prodotti/analisi-prezzi")}
            className="h-11 shrink-0 rounded-lg"
            data-testid="btn-analisi-prezzi"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Analisi Prezzi</span>
            <span className="sm:hidden">Analisi</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setExportOpen(true)}
            className="h-11 shrink-0 rounded-lg"
            data-testid="btn-export-acquisti"
          >
            <FileSpreadsheet className="h-4 w-4 text-emerald-600 sm:mr-2" />
            <span className="hidden sm:inline">Esporta Excel</span>
          </Button>
        </div>
      </div>

      <ExportAcquistiDialog open={exportOpen} onOpenChange={setExportOpen} />

      {/* Tab selector — pill stile Ordini/Fornitori */}
      <div className="flex gap-2">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            data-testid={`tab-${key}`}
            className={`h-14 px-5 rounded-2xl font-semibold flex items-center gap-2 transition-all text-sm ${
              activeTab === key
                ? "bg-primary text-white shadow-sm"
                : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{key === "food" ? "Food" : "Materiali"}</span>
          </button>
        ))}
      </div>

      <ProdottiTab tipo={activeTab} key={activeTab} openId={openId} onConsumeOpen={consumeOpen} />
    </div>
  );
};

export default ProdottiPage;
